import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Axios from "../../../_services/caller.service";
import styles from "./DiracLicence.module.scss";
import { FiCopy, FiColumns } from "react-icons/fi";
import { toast } from "react-toastify";
import Modal from "../../UiComponents/modals/Modal";
import useModal from "../../../hooks/useModal";
import { IoMdAddCircleOutline } from "react-icons/io";
import Select from "react-select";
import { PermissionGate } from "../../superAdmin/managePermissions/PermissionGate";

const DiracLicence = () => {
  const { t } = useTranslation();
  const [editRowId, setEditRowId] = useState(null);
  const [formData, setFormData] = useState({});
  const [tableData, setTableData] = useState([]);
  const [numberOfLines, setNumberOfLines] = useState(1);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [maj, setMaj] = useState(false);
  const [cabines, setCabines] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [allCabines, setAllCabines] = useState([]);
  const [showExtraCols, setShowExtraCols] = useState(false);

  const { isShowing, toggle } = useModal();

  const hiddenColumns = ["LicenceKey", "reductionCode", "Name"];
  const allColumns = [
    "ID",
    "CompteHS",
    "cabids",
    "Login",
    "mdp",
    "LicenceKey",
    "reductionCode",
    "Name",
    "Notes",
  ];
  const columnOrder = showExtraCols
    ? allColumns
    : allColumns.filter((c) => !hiddenColumns.includes(c));

  // Déclaration de la fonction getCabineNamesFromIds avant son utilisation
  const getCabineNamesFromIds = (cabIds) => {
    if (!cabIds) return "";
    const ids = cabIds.split(",").map((id) => parseInt(id.trim()));
    const cabineNames = ids.map((id) => {
      const cabine = allCabines.find((cab) => cab.id === id);
      return cabine ? cabine.cabname : "";
    });
    return cabineNames.filter((name) => name !== "").join(", ");
  };

  const sortedData = tableData.sort((a, b) => a.ID - b.ID);

  const filteredData = sortedData.filter((row) => {
    const cabineNamesString = getCabineNamesFromIds(row.cabids);
    return (
      (row.CompteHS || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      cabineNamesString.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  useEffect(() => {
    Axios.get("/diracLicence")
      .then((response) => {
        if (response.data && response.data.data) {
          setTableData(response.data.data);
        } else {
          console.error(
            "Erreur : Les données de licence ne sont pas disponibles."
          );
        }
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des licences :", error);
      });
  }, [maj]);

  useEffect(() => {
    Axios.get("/getAllUserForSuperAdmin")
      .then((response) => {
        if (response.data && response.data.data) {
          setUsers(response.data.data);
        } else {
          console.error(
            "Erreur : Les données des utilisateurs ne sont pas disponibles."
          );
        }
      })
      .catch((error) => {
        console.error(
          "Erreur lors de la récupération des utilisateurs :",
          error
        );
      });
  }, []);

  const handleEdit = (rowId, rowData) => {
    setEditRowId(rowId);
    const currentRowData = tableData.find((row) => row.ID === rowData.ID);
    if (!currentRowData) {
      console.error("Erreur : Données de la ligne introuvables", rowData.ID);
      return;
    }

    setFormData({
      ...currentRowData,
      cabids: currentRowData.cabids
        ? currentRowData.cabids
            .split(",")
            .map((cabId) => parseInt(cabId.trim()))
        : [],
    });

    const userId = currentRowData.user_id || null;
    setSelectedUserId(userId);
    if (!userId) setCabines([]);
  };

  const getUsedCabines = () => {
    const usedCabines = new Set();
    tableData.forEach((row) => {
      if (row.ID !== formData.ID && row.cabids) {
        row.cabids.split(",").forEach((cabId) => {
          usedCabines.add(parseInt(cabId.trim()));
        });
      }
    });
    return usedCabines;
  };
  const handleCompteHsChange = (selectedOption) => {
    const userId = selectedOption ? selectedOption.value : null;

    setFormData({
      ...formData,
      user_id: userId || null,
      cabids: !userId ? [] : formData.cabids,
    });

    if (!userId) {
      setCabines([]);
    }

    setSelectedUserId(userId);
  };

  const handleSave = () => {
    if (!formData.ID || !formData.cabids) {
      toast.error(t("license.form_incomplete"));
      return;
    }

    const data = { ...formData };
    data.cabids = Array.isArray(data.cabids)
      ? data.cabids.join(",")
      : data.cabids;

    Axios.put(`/updateDiracLicence/${data.ID}`, data)
      .then((response) => {
        const rowIndex = tableData.findIndex((item) => item.ID === data.ID);
        if (rowIndex !== -1) {
          const updatedDataList = tableData.map((item) =>
            item.ID === data.ID ? { ...item, ...data } : item
          );
          setTableData(updatedDataList);
        }
        toast.success(t("license.modification_success"));
        setEditRowId(null);
        setMaj(!maj);
      })
      .catch((error) => {
        console.error("Erreur lors de la mise à jour", error);
        toast.error(t("license.update_error"));
      });
  };

  const handleCabineChange = (selectedOptions, key) => {
    const selectedCabines = selectedOptions.map((option) => option.value);
    setFormData({ ...formData, [key]: selectedCabines });
  };

  const handleInputChange = (e, key) => {
    setFormData({ ...formData, [key]: e.target.value });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast("Copied to clipboard!");
      },
      (err) => {
        console.error("Could not copy text: ", err);
      }
    );
  };

  const submitLines = () => {
    const linesToCreate = Array.from({ length: numberOfLines }, () => ({
      reductionCode: "",
      Name: "",
      Login: "",
      mdp: "",
      LicenceKey: "",
      user_id: null,
      cabids: "",
      Notes: "",
    }));

    Axios.post("/createMultipleLicences", { lines: linesToCreate })
      .then((response) => {
        const updatedDataList = [...tableData, ...response.data.newLines];
        setTableData(updatedDataList);

        setEditRowId(null);
        setFormData({});
        toast.success(t("license.lines_created"));
        toggle();
      })
      .catch((error) => {
        console.error("Erreur lors de la création des lignes", error);
        toast.error(t("license.lines_creation_error"));
      });
  };

  useEffect(() => {
    if (selectedUserId) {
      Axios.get(`/getCabinesIdByCenterAndCabname/${selectedUserId}`)
        .then((response) => {
          if (response.data && response.data.data) {
            const usedCabines = getUsedCabines();
            const availableCabines = response.data.data.filter(
              (cab) => !usedCabines.has(cab.id)
            );
            setCabines(availableCabines);
          } else {
            console.error("Erreur : Les cabines ne sont pas disponibles.");
          }
        })
        .catch((error) => {
          console.error("Erreur lors de la récupération des cabines :", error);
        });
    }
  }, [selectedUserId, tableData]);

  useEffect(() => {
    Axios.get(`/allCabines/`)
      .then((response) => {
        if (response.data && response.data.data) {
          setAllCabines(response.data.data);
        } else {
          console.error(
            "Erreur : Les données des cabines ne sont pas disponibles."
          );
        }
      })
      .catch((error) => {
        console.error(
          "Erreur lors de la récupération de toutes les cabines :",
          error
        );
      });
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2>{t("license.dirac_title")}</h2>
        <div className={styles.actions}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t("license.search_placeholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <PermissionGate actionKey={"diracLicence.addNewLicence"}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnCreateNew}`}
              onClick={() => toggle()}
            >
              <IoMdAddCircleOutline /> {t("license.add_license")}
            </button>
          </PermissionGate>
          <button
            type="button"
            className={styles.btnToggleCols}
            onClick={() => setShowExtraCols((v) => !v)}
            title={showExtraCols ? t("license.toggle_columns_hide") : t("license.toggle_columns_show")}
          >
            <FiColumns /> {showExtraCols ? t("license.less_columns") : t("license.more_columns")}
          </button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
      <table className={`${styles.table} ${showExtraCols ? styles.tableExpanded : ''}`}>
        <thead>
          <tr>
            {columnOrder.map((colKey) => (
              <th key={colKey} className={styles.th}>
                {colKey}
              </th>
            ))}
            <th className={styles.thActions}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((row, index) => (
            <tr key={index} className={styles.tr}>
              {columnOrder.map((colKey) => {
                const value = row[colKey];

                if (colKey === "CompteHS" && editRowId === index) {
                  const selectOptions = [
                    { value: null, label: t("license.no_user") },
                    ...users.map((user) => ({
                      value: user.ID,
                      label: `${user.user_login} - ${user.ID}`,
                    })),
                  ];

                  return (
                    <td key={colKey} className={styles.td}>
                      <Select
                        options={selectOptions}
                        className={styles.selectUser}
                        value={selectOptions.find(
                          (option) => option.value === formData.user_id
                        )}
                        onChange={(selectedOption) =>
                          handleCompteHsChange(selectedOption)
                        }
                      />
                    </td>
                  );
                }

                if (colKey === "cabids") {
                  if (editRowId === index) {
                    const cabineOptions = cabines.map((cab) => ({
                      value: cab.id,
                      label: cab.cabname,
                    }));

                    return (
                      <td key={colKey} className={styles.td}>
                        <Select
                          isMulti
                          options={cabineOptions}
                          className={styles.selectCabine}
                          value={cabineOptions.filter((option) =>
                            formData.cabids.includes(option.value)
                          )}
                          onChange={(selectedOptions) =>
                            handleCabineChange(selectedOptions, "cabids")
                          }
                        />
                      </td>
                    );
                  } else {
                    return (
                      <td key={colKey} className={styles.tdWrap}>{getCabineNamesFromIds(row.cabids)}</td>
                    );
                  }
                }

                if (["Login", "mdp"].includes(colKey) && editRowId !== index) {
                  return (
                    <td key={colKey} className={styles.tdCopy}>
                      <div>
                        {value}
                        <FiCopy
                          className={`${styles.copyIcon} has-tooltip`}
                          aria-label={t("common.copy")}
                          onClick={() => copyToClipboard(value)}
                        />
                      </div>
                    </td>
                  );
                }

                if (colKey === "LicenceKey" && editRowId !== index) {
                  return (
                    <td key={colKey} className={styles.tdCopy} title={value || ""}>
                      <div>
                        <span className={styles.licenceKeyText}>{value}</span>
                        <FiCopy
                          className={`${styles.copyIcon} has-tooltip`}
                          aria-label={t("license.copy_key")}
                          onClick={() => copyToClipboard(value)}
                        />
                      </div>
                    </td>
                  );
                }

                if (colKey === "ID") {
                  return (
                    <td key={colKey} className={styles.td}>
                      {value}
                    </td>
                  );
                }

                return editRowId === index ? (
                  <td key={colKey} className={styles.td}>
                    <input
                      type="text"
                      className={styles.input}
                      value={formData[colKey]}
                      onChange={(e) => handleInputChange(e, colKey)}
                    />
                  </td>
                ) : (
                  <td
                    key={colKey}
                    className={colKey === "Notes" ? styles.tdWrap : styles.td}
                  >
                    {value}
                  </td>
                );
              })}
              <td className={styles.tdActions}>
                {editRowId === index ? (
                  <div className={styles.containerBtn}>
                    <button
                      className={styles.btnCancel}
                      onClick={() => {
                        setEditRowId(null);
                        setFormData({});
                      }}
                      type="button"
                    >
                      {t("common.cancel")}
                    </button>
                    <button
                      className={styles.btnSave}
                      onClick={() => handleSave(index)}
                      type="button"
                    >
                      {t("common.save")}
                    </button>
                  </div>
                ) : (
                  <PermissionGate actionKey={"diracLicence.updateLicence"}>
                    <button
                      className={styles.btnEdit}
                      onClick={() => handleEdit(index, row)}
                    >
                      {t("common.modify")}
                    </button>
                  </PermissionGate>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <Modal isShowing={isShowing} hide={toggle} title={t("license.add_license")}>
        <div className={styles.containerModal}>
          <h3 className={styles.modalTitle}>{t("license.how_many_licenses")}</h3>
          <div className={styles.modalField}>
            <label>{t("license.number_of_lines")}</label>
            <input
              type="number"
              min="1"
              className={styles.modalInput}
              value={numberOfLines}
              onChange={(e) => setNumberOfLines(e.target.value)}
              placeholder="1"
            />
          </div>
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.modalBtnCancel}
              onClick={toggle}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className={styles.modalBtnGenerate}
              onClick={submitLines}
            >
              {t("license.generate_lines")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DiracLicence;
